// Dashboard "Seasonal Sale" (Popcorn / Camp Card) panel — SHIPPED AS A
// MOCK. There is no real data model for unit sale commitments yet, so every
// number here is fabricated. The "Preview" badge below is load-bearing, not
// decorative — it's what stops this from silently looking live.
//
// Recommended real data model, not built this pass:
//   Collection `seasonalCommitments`, doc id `${program}__${year}__${districtId}`
//   (mirrors fosGoals' `${year}__${districtId}` id pattern). Fields:
//   program: 'popcorn' | 'campCard', year, districtId, unitsCommitted: number,
//   updatedAt, updatedBy.
//   Source: manual entry per district to start (same pattern as FOS goal
//   entry — a small form scoped to the editor's own district). A bulk
//   import or council-system sync is a plausible later upgrade once a real
//   external source is identified, not a blocker for shipping manual entry
//   first.
//   Access: model it on districtSummaries' rule — `allow read: if
//   isAdmin() || (hasProfile() && resource.data.districtId ==
//   myDistrict());` — with writes open to District Executive / Committee
//   Chair / admin for their own district, same district-scoped edit
//   pattern used everywhere else in Compass.
// See also docs/seasonal-sale-data-model.md.
import { useMemo } from 'react';
import { useAppContext } from '../../components/Layout';
import type { SeasonalProgram } from '../../types';

const PROGRAM_LABEL: Record<SeasonalProgram, string> = {
  popcorn: 'Popcorn Sale',
  campCard: 'Camp Card Sale',
};

/** Deterministic (not random) fabricated counts, seeded off each district's
 *  own id/name so the numbers don't reshuffle on every render. */
function fakeCommitted(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return 4 + (h % 14); // 4..17 units — a plausible per-district range
}

export default function SeasonalSalePanel() {
  const { settings } = useAppContext();
  const program = settings.activeSeasonalProgram;
  const year = new Date().getFullYear();

  const rows = useMemo(
    () =>
      settings.districts
        .map((d) => ({ id: d.id, name: d.name, committed: fakeCommitted(program + d.id) }))
        .sort((a, b) => b.committed - a.committed),
    [settings.districts, program],
  );

  const total = rows.reduce((s, r) => s + r.committed, 0);
  const maxCommitted = Math.max(1, ...rows.map((r) => r.committed));

  return (
    <section className="card dash-card">
      <header className="dash-card-head">
        <div>
          <h3>{PROGRAM_LABEL[program]}</h3>
          <p className="dash-card-sub">{year} sale · units committed to sell</p>
        </div>
        <span className="dash-preview-badge" title="Not wired to real data yet">
          Preview
        </span>
      </header>

      <div className="dash-bignum">
        <strong>{total}</strong>
        <span>units committed, council-wide</span>
      </div>

      <ul className="dash-relbar-list">
        {rows.map((r) => (
          <li key={r.id} className="dash-relbar-row">
            <span className="dash-relbar-name">{r.name}</span>
            <span className="dash-relbar-track">
              <span
                className="dash-relbar-fill"
                style={{ width: `${(r.committed / maxCommitted) * 100}%` }}
              />
            </span>
            <span className="dash-relbar-count">{r.committed}</span>
          </li>
        ))}
      </ul>

      <p className="dash-card-sub" style={{ marginTop: 10 }}>
        Preview data — not wired to a real sale tracker yet.
      </p>
    </section>
  );
}
